import { AnimatePresence, motion } from "motion/react"
import { useCallback, useEffect, useId, useRef, useState } from "react"
import { neonHeadingClass } from "@/components/ui/neon"
import { SharedButton } from "@/components/ui/shared-button"
import { ShootingStars } from "@/components/ui/shooting-stars"
import { StarsBackground } from "@/components/ui/stars-background"
import { cn } from "@/lib/utils"

const SESSION_KEY = "eklo-portal-shown"

if (typeof window !== "undefined") {
  const nav = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined
  if (nav?.type === "reload") {
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {}
  }
}

function readShown() {
  if (typeof window === "undefined") return false
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1"
  } catch {
    return false
  }
}

function writeShown() {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(SESSION_KEY, "1")
  } catch {}
}

export function PortalIntro() {
  const [open, setOpen] = useState(true)
  const [warping, setWarping] = useState(false)
  const titleId = useId()
  const ctaRef = useRef<HTMLButtonElement | null>(null)
  const previouslyFocused = useRef<Element | null>(null)

  const close = useCallback(() => {
    writeShown()
    setOpen(false)
  }, [])

  useEffect(() => {
    if (readShown()) setOpen(false)
  }, [])

  useEffect(() => {
    if (!open) return

    previouslyFocused.current = document.activeElement
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.scrollTo(0, 0)

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)

    const focusTimer = window.setTimeout(() => ctaRef.current?.focus(), 250)

    return () => {
      window.removeEventListener("keydown", onKey)
      window.clearTimeout(focusTimer)
      document.body.style.overflow = prevOverflow
      const prev = previouslyFocused.current
      if (prev instanceof HTMLElement) prev.focus()
    }
  }, [open, close])

  const handleCta = () => {
    setWarping(true)
    window.setTimeout(() => {
      window.scrollTo(0, 0)
      close()
    }, 1300)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          onClick={close}
          className="fixed inset-0 z-100 flex min-h-svh items-center justify-center overflow-hidden bg-background"
        >
          <StarsBackground />
          <ShootingStars />

          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-5 bg-[radial-gradient(circle_at_50%_50%,rgb(var(--brand-rgb)/0.22),transparent_60%)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: warping ? 0.6 : 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />

          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-6 bg-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: warping ? 0.55 : 0 }}
            transition={{ duration: 1.2, ease: "easeIn" }}
          />

          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 16 }}
            animate={
              warping
                ? { opacity: 0, scale: 1.08 }
                : { opacity: 1, y: 0, scale: 1 }
            }
            exit={{ opacity: 0, y: -8 }}
            transition={
              warping
                ? { duration: 0.6, ease: "easeIn" }
                : { duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }
            }
            className="relative z-10 mx-auto flex w-full max-w-3xl flex-col items-center gap-6 px-6 text-center sm:gap-8 sm:px-8"
          >
            <div
              id={titleId}
              className="flex flex-col items-center gap-3 sm:gap-4"
            >
              <p className="font-medium font-mono text-[clamp(0.85rem,1.8vw,1.15rem)] text-white uppercase tracking-[0.32em]">
                Enter the
              </p>
              <h2
                className={cn(
                  neonHeadingClass,
                  "text-[clamp(1.9rem,6.4vw,4.2rem)] leading-[1] tracking-tight"
                )}
              >
                EKLO-Verse
              </h2>
            </div>

            <SharedButton
              ref={ctaRef}
              size="lg"
              type="button"
              onClick={handleCta}
              className="w-full max-w-xs sm:w-fit"
            >
              Start the journey
            </SharedButton>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
